import React from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import { SortArrowsIcon } from "../MyAppsTable/icons";
import { DataCards } from "../DataTable/DataCards";
import { DataTableColumn } from "../DataTable/types";
import useIsPhone from "../../../hooks/useIsPhone";
import { SettingsTableColumn, SettingsTableProps } from "./types";

/** The table used by the settings screens: Team and Notifications. */
export function SettingsTable<K extends string, R>({
  columns,
  rows,
  rowKey,
  renderCell,
  sort,
  onSortChange,
  minWidth = 720,
  rowHeight = 64,
  emptyText = "Nothing here yet.",
  cards,
  className,
}: SettingsTableProps<K, R>) {
  const isPhone = useIsPhone();
  const sortable = (c: SettingsTableColumn<K>) =>
    Boolean(c.label) &&
    !c.header &&
    c.sortable !== false &&
    Boolean(onSortChange);

  const toggle = (key: K) =>
    onSortChange?.({ key, asc: sort?.key === key ? !sort.asc : true });

  if (isPhone && cards) {
    const place = (key: K): DataTableColumn<R>["mobile"] => {
      if (key === cards.title) return "title";
      if (cards.header?.includes(key)) return "header";
      if (cards.footer?.includes(key)) return "footer";
      if (cards.full?.includes(key)) return "full";
      if (cards.hidden?.includes(key)) return "hidden";
      return undefined;
    };
    return (
      <DataCards<R>
        className={className}
        columns={columns.map((c) => ({
          key: c.key,
          title: c.label ?? "",
          width: 0,
          sortable: sortable(c),
          headerStart: c.header,
          render: (row: R) => renderCell(row, c.key),
          mobile: place(c.key),
        }))}
        rows={rows}
        rowKey={rowKey}
        sort={
          sort ? { key: sort.key, direction: sort.asc ? "asc" : "desc" } : null
        }
        onSortChange={
          onSortChange &&
          ((next) =>
            onSortChange(
              next
                ? { key: next.key as K, asc: next.direction === "asc" }
                : null,
            ))
        }
        emptyText={emptyText}
      />
    );
  }

  return (
    <div className={cs(styles.wrap, className)}>
      <table className={styles.table} style={{ minWidth }}>
        <colgroup>
          {columns.map((c) => (
            <col key={c.key} style={c.width ? { width: c.width } : undefined} />
          ))}
        </colgroup>

        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} aria-label={c.label ? undefined : "Actions"}>
                {sortable(c) ? (
                  <button
                    type="button"
                    className={cs(styles.headBtn, {
                      [styles.headBtnActive]: sort?.key === c.key,
                    })}
                    onClick={() => toggle(c.key)}
                  >
                    {c.label}
                    <SortArrowsIcon />
                  </button>
                ) : (
                  (c.header ?? c.label)
                )}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)} style={{ height: rowHeight }}>
              {columns.map((c) => (
                <td key={c.key}>{renderCell(row, c.key)}</td>
              ))}
            </tr>
          ))}

          {!rows.length && (
            <tr>
              <td colSpan={columns.length} className={styles.empty}>
                {emptyText}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default SettingsTable;
